import type { PaymentMethodDto, SubscriptionSummaryDto } from 'src/lib/api';
import type {
  TrialKind,
  BillingState,
  BillingInterval,
  SubscriptionWarning,
} from 'src/sections/settings/billing/types/billing';

import { TRIAL_KINDS, BILLING_INTERVALS } from 'src/sections/settings/billing/types/billing';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

const STATUS_BILLING_STATES: Partial<Record<string, BillingState>> = {
  canceled: 'expired',
  cancelled: 'expired',
  incomplete_expired: 'expired',
  active: 'active',
  trialing: 'active',
  past_due: 'active',
  unpaid: 'active',
  incomplete: 'active',
  paused: 'active',
};

const STATUS_WARNINGS: Partial<Record<string, SubscriptionWarning>> = {
  past_due: 'past_due',
  unpaid: 'unpaid',
  incomplete: 'incomplete',
  incomplete_expired: 'incomplete_expired',
  paused: 'paused',
  trialing: 'trialing',
};

export function deriveBillingState(
  subscription: SubscriptionSummaryDto | null,
  optimisticCancelAtPeriodEnd: boolean
): BillingState {
  if (!subscription) return 'free';

  const state = STATUS_BILLING_STATES[subscription.Status.toLowerCase()];
  if (state === 'expired') return 'expired';
  if (optimisticCancelAtPeriodEnd || subscription.CancelAtPeriodEnd) return 'cancelled';
  return state ?? 'free';
}

/** A card stays valid through the end of its expiry month, so it lapses on the first of the next. */
export function isCardExpiringSoon({ ExpMonth, ExpYear }: PaymentMethodDto): boolean {
  if (!ExpMonth || !ExpYear) return false;
  return new Date(ExpYear, ExpMonth).getTime() - Date.now() < THIRTY_DAYS_MS;
}

export function deriveSubscriptionWarning(
  subscription: SubscriptionSummaryDto | null,
  paymentMethod: PaymentMethodDto | null
): SubscriptionWarning {
  const statusWarning = subscription && STATUS_WARNINGS[subscription.Status.toLowerCase()];
  if (statusWarning) return statusWarning;

  return paymentMethod && isCardExpiringSoon(paymentMethod) ? 'card_expiring' : null;
}

export function trialSourceOf(subscription: SubscriptionSummaryDto): TrialKind | null {
  const source = subscription.TrialSource?.toLowerCase();
  return TRIAL_KINDS.find((kind) => kind === source) ?? null;
}

export function billingIntervalOf(subscription: SubscriptionSummaryDto): BillingInterval | null {
  const cadence = subscription.BillingInterval?.toLowerCase();
  return BILLING_INTERVALS.find((interval) => interval === cadence) ?? null;
}

// ----------------------------------------------------------------------
// Field readers for the coupon payloads normalized in utils/coupon.ts, which are read untyped:
// each field is accepted in either casing.
// ----------------------------------------------------------------------

const pascalOf = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

export function readString(o: Record<string, unknown>, key: string): string | null {
  const v = o[key] ?? o[pascalOf(key)];
  return v == null ? null : String(v);
}

export function readNumber(o: Record<string, unknown>, key: string, fallback = 0): number {
  const v = o[key] ?? o[pascalOf(key)];
  if (v == null) return fallback;
  const n = Number(v);
  // A non-numeric value would otherwise render as "NaN credits".
  return Number.isFinite(n) ? n : fallback;
}

export function readBool(o: Record<string, unknown>, key: string, fallback = false): boolean {
  const v = o[key] ?? o[pascalOf(key)];
  if (v == null) return fallback;
  // Guard the string case explicitly: Boolean('false') is true.
  if (typeof v === 'string') return v.toLowerCase() === 'true';
  return Boolean(v);
}
